import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ChatWidget from '@/components/feature/ChatWidget';
import { industryNavData } from '@/mocks/industrySolutionsData';

interface IndustryData {
  id: string;
  title: string;
  subtitle: string;
  heroImage: string;
  stats: { value: string; label: string; trend: string }[];
  painPoints: { icon: string; title: string; description: string }[];
  features: { icon: string; title: string; description: string; highlights: string[] }[];
  architecture: { title: string; description: string; layers: { name: string; items: string[] }[] };
  caseStudy: {
    company: string;
    industry: string;
    logo: string;
    result: string;
    metric: string;
    period: string;
    challenge: string;
    solution: string;
    results: { label: string; value: string }[];
    quote: string;
    quoteAuthor: string;
    quoteTitle: string;
    quoteAvatar: string;
  };
  ctaTitle: string;
  ctaSubtitle: string;
}

export default function IndustrySolutionPage({ data }: { data: IndustryData }) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('animate-in');
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [data.id]);

  const handleDemoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      document.querySelector('#demo')?.scrollIntoView({ behavior: 'smooth' });
    }, 150);
  };

  return (
    <div className="min-h-screen bg-firmai-dark" ref={sectionRef}>
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative pt-24 md:pt-32 pb-16 md:pb-24 overflow-hidden">
          <div className="absolute inset-0">
            <img src={data.heroImage} alt={data.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-firmai-dark/70 via-firmai-dark/80 to-firmai-dark" />
            <div className="absolute inset-0 bg-gradient-to-r from-firmai-dark/60 to-transparent" />
          </div>
          <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-firmai-green/5 rounded-full blur-3xl" />

          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl">
              <div className="reveal opacity-0 translate-y-6 transition-all duration-700">
                <span className="text-firmai-green text-sm font-semibold tracking-wider uppercase">行业解决方案</span>
                <h1 className="font-display font-bold text-3xl md:text-5xl lg:text-6xl text-white mt-4 leading-tight">
                  {data.title}
                </h1>
                <p className="text-firmai-text mt-4 max-w-2xl text-base md:text-lg leading-relaxed">
                  {data.subtitle}
                </p>
              </div>

              {/* Stats */}
              <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-100 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-10">
                {data.stats.map((stat, index) => (
                  <div
                    key={stat.label}
                    className="bg-firmai-card/80 backdrop-blur-sm border border-firmai-border rounded-xl p-4 md:p-5 hover:border-firmai-green/30 transition-all duration-300"
                    style={{ transitionDelay: `${index * 80}ms` }}
                  >
                    <div className="text-firmai-green font-display font-bold text-xl md:text-2xl">{stat.value}</div>
                    <div className="text-firmai-muted text-xs mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Industry Nav */}
        <section className="relative py-4 bg-firmai-darker border-y border-firmai-border">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              <span className="text-firmai-muted text-xs font-medium mr-2">行业方案:</span>
              {industryNavData.map((item) => (
                <a
                  key={item.id}
                  href={item.href}
                  onClick={(e) => { e.preventDefault(); navigate(item.href); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                    location.pathname === item.href
                      ? 'bg-firmai-green text-firmai-dark'
                      : 'bg-firmai-card border border-firmai-border text-firmai-text hover:border-firmai-green/30 hover:text-white'
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={item.icon} />
                  </span>
                  {item.label}
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Pain Points */}
        <section className="relative py-16 md:py-24 bg-firmai-dark">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 text-center mb-10 md:mb-14">
              <span className="text-firmai-green text-sm font-semibold tracking-wider uppercase">行业痛点</span>
              <h2 className="font-display font-bold text-2xl md:text-4xl text-white mt-3">
                我们理解您的挑战
              </h2>
              <p className="text-firmai-text mt-3 max-w-xl mx-auto text-sm md:text-base">
                深耕行业多年，我们精准把握每个行业的能源管理核心难题
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 max-w-6xl mx-auto">
              {data.painPoints.map((point, index) => (
                <div
                  key={point.title}
                  className="reveal opacity-0 translate-y-6 transition-all duration-700 bg-firmai-card border border-firmai-border rounded-xl p-5 md:p-6 hover:border-firmai-green/20 transition-all duration-300 group"
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="w-11 h-11 flex items-center justify-center bg-firmai-dark border border-firmai-border rounded-lg group-hover:border-firmai-green/30 transition-all duration-300">
                    <span className="w-5 h-5 flex items-center justify-center text-firmai-green">
                      <i className={point.icon} />
                    </span>
                  </div>
                  <h3 className="text-white font-semibold text-sm md:text-base mt-4">{point.title}</h3>
                  <p className="text-firmai-text text-xs md:text-sm mt-2 leading-relaxed">{point.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="relative py-16 md:py-24 bg-firmai-darker">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,217,163,0.04)_0%,transparent_70%)]" />
          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 text-center mb-10 md:mb-14">
              <span className="text-firmai-green text-sm font-semibold tracking-wider uppercase">核心功能</span>
              <h2 className="font-display font-bold text-2xl md:text-4xl text-white mt-3">
                专为行业定制的解决方案
              </h2>
              <p className="text-firmai-text mt-3 max-w-xl mx-auto text-sm md:text-base">
                基于行业特性深度定制，每个功能都直击业务核心需求
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 max-w-6xl mx-auto">
              {data.features.map((feature, index) => (
                <div
                  key={feature.title}
                  className="reveal opacity-0 translate-y-6 transition-all duration-700 bg-firmai-card border border-firmai-border rounded-xl p-5 md:p-6 hover:border-firmai-green/20 transition-all duration-300 group"
                  style={{ transitionDelay: `${index * 80}ms` }}
                >
                  <div className="w-11 h-11 flex items-center justify-center bg-gradient-to-br from-firmai-green/15 to-firmai-blue/15 border border-firmai-green/20 rounded-lg group-hover:scale-105 transition-transform duration-300">
                    <span className="w-5 h-5 flex items-center justify-center text-firmai-green">
                      <i className={feature.icon} />
                    </span>
                  </div>
                  <h3 className="text-white font-semibold text-sm md:text-base mt-4">{feature.title}</h3>
                  <p className="text-firmai-text text-xs md:text-sm mt-2 leading-relaxed">{feature.description}</p>
                  <div className="flex flex-wrap gap-1.5 mt-4">
                    {feature.highlights.map((h) => (
                      <span key={h} className="px-2 py-0.5 bg-firmai-dark border border-firmai-border rounded-full text-firmai-green text-xs">
                        {h}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Architecture */}
        <section className="relative py-16 md:py-24 bg-firmai-dark">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 text-center mb-10 md:mb-14">
              <span className="text-firmai-green text-sm font-semibold tracking-wider uppercase">技术架构</span>
              <h2 className="font-display font-bold text-2xl md:text-4xl text-white mt-3">
                {data.architecture.title}
              </h2>
              <p className="text-firmai-text mt-3 max-w-xl mx-auto text-sm md:text-base">
                {data.architecture.description}
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-3">
              {data.architecture.layers.map((layer, index) => (
                <div
                  key={layer.name}
                  className="reveal opacity-0 translate-y-6 transition-all duration-700 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5 bg-firmai-card border border-firmai-border rounded-xl p-4 md:p-5"
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="shrink-0 px-4 py-2 bg-firmai-dark border border-firmai-border rounded-lg">
                    <span className="text-firmai-green font-semibold text-sm whitespace-nowrap">{layer.name}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {layer.items.map((item) => (
                      <span key={item} className="px-3 py-1.5 bg-firmai-dark border border-firmai-border rounded-lg text-firmai-text text-xs hover:border-firmai-green/20 transition-all duration-200">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Case Study */}
        <section className="relative py-16 md:py-24 bg-firmai-darker">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,217,163,0.04)_0%,transparent_70%)]" />
          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 text-center mb-10 md:mb-14">
              <span className="text-firmai-green text-sm font-semibold tracking-wider uppercase">客户案例</span>
              <h2 className="font-display font-bold text-2xl md:text-4xl text-white mt-3">
                真实客户，真实成果
              </h2>
            </div>

            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 max-w-5xl mx-auto bg-firmai-card border border-firmai-border rounded-xl overflow-hidden">
              <div className="flex flex-col lg:flex-row">
                {/* Left - Image & Summary */}
                <div className="w-full lg:w-[45%] relative">
                  <img src={data.caseStudy.logo} alt={data.caseStudy.company} className="w-full h-56 lg:h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-firmai-card via-firmai-card/30 to-transparent lg:bg-gradient-to-r" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="px-3 py-1 bg-firmai-green/20 text-firmai-green text-xs font-medium rounded-full border border-firmai-green/30 backdrop-blur-sm inline-block mb-2">
                      {data.caseStudy.industry}
                    </div>
                    <h3 className="text-white font-semibold text-lg">{data.caseStudy.company}</h3>
                    <p className="text-firmai-muted text-xs mt-1">{data.caseStudy.period}</p>
                  </div>
                </div>

                {/* Right - Details */}
                <div className="w-full lg:w-[55%] p-5 md:p-8 space-y-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-firmai-green font-display font-bold text-2xl md:text-3xl">{data.caseStudy.result}</div>
                      <div className="text-firmai-muted text-sm">{data.caseStudy.metric}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-firmai-dark/50 border border-firmai-border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-4 h-4 flex items-center justify-center text-red-400"><i className="ri-error-warning-line" /></span>
                        <span className="text-white text-xs font-medium">面临挑战</span>
                      </div>
                      <p className="text-firmai-text text-xs leading-relaxed">{data.caseStudy.challenge}</p>
                    </div>
                    <div className="bg-firmai-dark/50 border border-firmai-border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-4 h-4 flex items-center justify-center text-firmai-green"><i className="ri-lightbulb-line" /></span>
                        <span className="text-white text-xs font-medium">解决方案</span>
                      </div>
                      <p className="text-firmai-text text-xs leading-relaxed">{data.caseStudy.solution}</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-white text-xs font-medium mb-3 flex items-center gap-2">
                      <span className="w-4 h-4 flex items-center justify-center text-firmai-green"><i className="ri-bar-chart-box-line" /></span>
                      关键成果
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {data.caseStudy.results.map((r) => (
                        <div key={r.label} className="bg-firmai-dark border border-firmai-border rounded-lg p-3 text-center hover:border-firmai-green/20 transition-all duration-200">
                          <div className="text-firmai-green font-display font-bold text-sm md:text-base">{r.value}</div>
                          <div className="text-firmai-muted text-xs mt-0.5">{r.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-firmai-dark/50 border border-firmai-border rounded-lg p-4">
                    <div className="relative">
                      <span className="absolute -top-1 -left-1 text-firmai-green/20 text-2xl font-serif">&ldquo;</span>
                      <p className="text-firmai-text text-xs md:text-sm leading-relaxed pl-3">{data.caseStudy.quote}</p>
                    </div>
                    <div className="flex items-center gap-3 mt-3 pt-3 border-t border-firmai-border">
                      <img src={data.caseStudy.quoteAvatar} alt={data.caseStudy.quoteAuthor} className="w-8 h-8 rounded-full object-cover" />
                      <div>
                        <div className="text-white text-xs font-medium">{data.caseStudy.quoteAuthor}</div>
                        <div className="text-firmai-muted text-xs">{data.caseStudy.quoteTitle}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-16 md:py-24 bg-firmai-dark">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,217,163,0.06)_0%,transparent_70%)]" />
          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 text-center">
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 max-w-2xl mx-auto">
              <h2 className="font-display font-bold text-2xl md:text-4xl text-white">{data.ctaTitle}</h2>
              <p className="text-firmai-text mt-4 text-base md:text-lg">{data.ctaSubtitle}</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
                <a
                  href="/cases"
                  onClick={(e) => { e.preventDefault(); navigate('/cases'); }}
                  className="inline-flex items-center gap-2 px-8 py-3.5 border border-white/30 text-white font-medium rounded-md hover:border-firmai-green hover:text-firmai-green transition-all duration-200 whitespace-nowrap group"
                >
                  查看更多案例
                  <span className="w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform duration-200"><i className="ri-arrow-right-line" /></span>
                </a>
                <button
                  onClick={handleDemoClick}
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-firmai-green text-firmai-dark font-semibold rounded-md hover:bg-firmai-green/90 hover:scale-105 active:scale-95 transition-all duration-200 whitespace-nowrap"
                >
                  <span className="w-5 h-5 flex items-center justify-center"><i className="ri-calendar-line" /></span>
                  预约演示
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ChatWidget />

      <style>{`
        .reveal.animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
      `}</style>
    </div>
  );
}